import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  IconButton,
  FormHelperText,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  CardActions,
  Pagination,
  useMediaQuery,
  useTheme,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Chip,
  Autocomplete
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon,
  Delete as DeleteIcon, 
  Visibility as VisibilityIcon,
  Upload as UploadIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MaterialType, purchaseApi, CreatePurchaseDTO, CreateMaterialInwardDTO } from '../api/materials';
import purchaseOrdersApi, { VendorAvailablePO, VendorAvailablePOItem } from '../api/purchaseOrders';
import api, { getMaterialQualities } from '../services/api';
import { format } from 'date-fns';
import vendorApi, { Vendor } from '../api/vendor';
import { MATERIAL_UOMS } from '../constants/materials';

// Quality options loaded from settings API (no hardcoded list)

// Material constants defined inline
const MATERIAL_CONSTANTS = {
  TYPES: {
    FABRIC: 'Fabric',
    LACE: 'Lace', 
    BUTTON: 'Button'
  },
  UOMS: MATERIAL_UOMS
};

// Initial material item structure
const emptyMaterial: CreateMaterialInwardDTO = {
  material_type: MaterialType.FABRIC,
  quantity: 0,
  uom: 'mtr',
  rate: 0,
  amount: 0,
  material_quality: '',
  is_sample: false,
  image_url: '',
  fabric_width: null,
  fabric_L: null,
  material_code: ''
};

const PurchasePage: React.FC = () => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [vendors, setVendors] = useState<any[]>([]); // SUPPLIER type only — used in purchase form dropdown
  const [purchases, setPurchases] = useState<any[]>([]);
  
  const [selectedPurchase, setSelectedPurchase] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Purchase form state
  const [purchaseData, setPurchaseData] = useState<CreatePurchaseDTO>({
    vendor_id: 0,
    invoice_id: '',
    invoice_date: new Date().toISOString().split('T')[0],
    received_date: new Date().toISOString().split('T')[0],
    total_amount: 0,
    notes: '',
    material_inwards: [{ ...emptyMaterial }]
  });
  
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [uploadingImage, setUploadingImage] = useState<{[key: number]: boolean}>({});

  // QUICK FIX: Add state for update mode
  const [updateMode, setUpdateMode] = useState('header_and_new');

  // PO linking state
  const [linkedPO, setLinkedPO] = useState<VendorAvailablePO | null>(null);
  const [poItemsDialogOpen, setPoItemsDialogOpen] = useState(false);
  const [vendorPoItems, setVendorPoItems] = useState<VendorAvailablePO[]>([]);
  const [poItemSelections, setPoItemSelections] = useState<Record<string, { selected: boolean; quantity: number }>>({});

  // Add state for purchases search
  const [purchaseSearch, setPurchaseSearch] = useState('');

  // Add state for purchases pagination
  const [purchasePage, setPurchasePage] = useState(1);
  const [purchasePageSize, setPurchasePageSize] = useState(10);
  const [purchaseTotal, setPurchaseTotal] = useState(0);

  // Material quality options for autocomplete
  const [materialQualityOptions, setMaterialQualityOptions] = useState<string[]>([]);
  
  // Load material quality options from settings table
  useEffect(() => {
    const fetchMaterialQualities = async () => {
      try {
        const response = await getMaterialQualities();
        const qualities: string[] = (Array.isArray(response.data) ? response.data : [])
          .filter((q: any) => q.isActive)
          .map((q: any) => q.name as string)
          .sort((a: string, b: string) => a.localeCompare(b));
        setMaterialQualityOptions(qualities);
      } catch (err) {
        console.error('Error fetching material quality options:', err);
      }
    };
    fetchMaterialQualities();
  }, []);

  // Fetch SUPPLIER vendors for purchase form dropdown
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const response = await vendorApi.getAll({ vendor_type: 'SUPPLIER' });
        setVendors(response.data);
      } catch (err) {
        console.error('Error fetching vendors:', err);
      }
    };
    fetchVendors();
  }, []);

  // Fetch purchases when the component mounts or after a new purchase is created
  useEffect(() => {
    fetchPurchases();
  }, []);

  // Open view dialog when navigated with viewPurchaseId
  useEffect(() => {
    const state = location.state as any;
    const viewId = state?.viewPurchaseId;
    if (viewId) {
      purchaseApi.getById(viewId).then(resp => {
        setSelectedPurchase(resp.data);
        setViewDialogOpen(true);
      }).catch(() => {
        // ignore error, dialog won't open
      });
    }
  }, [location.state]);

  const fetchPurchases = async (page = 1, pageSize = 10) => {
    try {
      setLoading(true);
      const response = await purchaseApi.getAll({ page, pageSize });
      if (response.data && Array.isArray(response.data.data)) {
        setPurchases(response.data.data);
        setPurchaseTotal(response.data.total || response.data.data.length);
        setPurchasePage(response.data.page || 1);
        setPurchasePageSize(response.data.pageSize || 10);
      } else if (Array.isArray(response.data)) {
        setPurchases(response.data);
        setPurchaseTotal(response.data.length);
        setPurchasePage(1);
        setPurchasePageSize(response.data.length);
      } else {
        setPurchases([]);
        setPurchaseTotal(0);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching purchases:', err);
      setError('Failed to load purchases. Please try again later.');
      setLoading(false);
    }
  };

  // Fetch purchases when page changes
  useEffect(() => {
    fetchPurchases(purchasePage, purchasePageSize);
    // eslint-disable-next-line
  }, [purchasePage, purchasePageSize]);

  // Ensure search scans across all pages by expanding page size during search
  useEffect(() => {
    const term = purchaseSearch.trim();
    if (term) {
      // Expand to a very large page size to include all results client-side
      if (purchasePageSize !== 100000 || purchasePage !== 1) {
        setPurchasePage(1);
        setPurchasePageSize(100000);
      }
    } else {
      // Restore default pagination when clearing search
      if (purchasePageSize !== 10 || purchasePage !== 1) {
        setPurchasePage(1);
        setPurchasePageSize(10);
      }
    }
    // We intentionally do not call fetchPurchases here; the pagination effect above will handle it.
    // eslint-disable-next-line
  }, [purchaseSearch]);

  const handleNewPurchase = () => {
    setIsEditMode(false);
    setPurchaseData({
      vendor_id: 0,
      invoice_id: '',
      invoice_date: new Date().toISOString().split('T')[0],
      received_date: new Date().toISOString().split('T')[0],
      total_amount: 0,
      notes: '',
      material_inwards: [{ ...emptyMaterial }]
    });
    setOpenDialog(true);
  };
  
  const handleEditPurchase = async (purchase: any) => {
    try {
      setLoading(true);
      
      // Fetch complete purchase details first
      const response = await purchaseApi.getById(purchase.id);
      const fullPurchaseData = response.data;
      
      console.log("Complete purchase data for edit:", fullPurchaseData);
      
      setIsEditMode(true);
      setSelectedPurchase(fullPurchaseData);
      
      // Initialize with existing purchase data
      const editData = {
        id: fullPurchaseData.id,
        vendor_id: fullPurchaseData.vendor_id,
        invoice_id: fullPurchaseData.invoice_id,
        invoice_date: format(new Date(fullPurchaseData.invoice_date), 'yyyy-MM-dd'),
        received_date: format(new Date(fullPurchaseData.received_date), 'yyyy-MM-dd'),
        total_amount: fullPurchaseData.total_amount,
        notes: fullPurchaseData.notes || '',
        material_inwards: fullPurchaseData.material_inwards?.map((material: any) => ({
          id: material.id,
          material_code: material.material_code,
          material_type: material.material_type,
          quantity: material.quantity,
          uom: material.uom,
          rate: material.rate,
          amount: material.amount,
          material_quality: material.material_quality || '',
          is_sample: material.is_sample || false,
          image_url: material.image_url || '',
          fabric_width: material.fabric_width || null,
          fabric_L: material.fabric_L || null
        })) || [{ ...emptyMaterial }]
      };
      
      setPurchaseData(editData);
      setOpenDialog(true);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching purchase details:", err);
      setError("Could not load purchase details for editing");
      setLoading(false);
    }
  };
  
  const handleViewPurchase = async (purchase: any) => {
    try {
      setLoading(true);
      
      // Fetch complete purchase details
      const response = await purchaseApi.getById(purchase.id);
      const fullPurchaseData = response.data;
      
      console.log("Complete purchase data for view:", fullPurchaseData);
      
      setSelectedPurchase(fullPurchaseData);
      setViewDialogOpen(true);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching purchase details:", err);
      setError("Could not load purchase details for viewing");
      setLoading(false);
    }
  };
  
  const handleDeleteClick = (purchase: any) => {
    setSelectedPurchase(purchase);
    setDeleteDialogOpen(true);
  };
  
  const handleDeletePurchase = async () => {
    if (!selectedPurchase) return;
    
    try {
      setLoading(true);
      await purchaseApi.delete(selectedPurchase.id);
      setSuccess('Purchase deleted successfully');
      setLoading(false);
      fetchPurchases();
      setDeleteDialogOpen(false);
      setSelectedPurchase(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete purchase');
      setLoading(false);
    }
  };
  
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormErrors({});
    setError(null);
    setSuccess(null);
  };

  const handleSelectChange = (event: SelectChangeEvent<any>) => {
    const { name, value } = event.target;
    setPurchaseData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTextInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setPurchaseData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // When vendor changes, clear existing PO linkage
  useEffect(() => {
    if (!purchaseData.vendor_id) {
      setLinkedPO(null);
      setVendorPoItems([]);
      setPoItemSelections({});
      return;
    }
  }, [purchaseData.vendor_id]);

  const openPoItemsDialog = async () => {
    if (!purchaseData.vendor_id) return;
    try {
      setLoading(true);
      const response = await purchaseOrdersApi.getVendorAvailableItems(purchaseData.vendor_id);
      const data: VendorAvailablePO[] = response.data || [];
      setVendorPoItems(data);
      setPoItemsDialogOpen(true);
    } catch (err) {
      console.error('Error fetching vendor PO items:', err);
      setError('Failed to fetch pending PO items for this vendor');
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const togglePoItemSelection = (poId: number, item: VendorAvailablePOItem, checked: boolean) => {
    const key = `${poId}-${item.id}`;
    setPoItemSelections(prev => {
      const next = { ...prev };
      if (checked) {
        next[key] = {
          selected: true,
          quantity: item.remaining_qty || item.quantity
        };
      } else {
        delete next[key];
      }
      return next;
    });

    if (checked) {
      const po = vendorPoItems.find(p => p.id === poId) || null;
      setLinkedPO(po);
    }
  };

  const updatePoItemSelectionQty = (poId: number, item: VendorAvailablePOItem, qty: number) => {
    const key = `${poId}-${item.id}`;
    setPoItemSelections(prev => ({
      ...prev,
      [key]: {
        selected: prev[key]?.selected ?? true,
        quantity: qty
      }
    }));
  };

  const applySelectedPoItemsToMaterials = () => {
    if (!linkedPO) {
      setPoItemsDialogOpen(false);
      return;
    }

    const currentMaterials = purchaseData.material_inwards || [];
    const newMaterials: CreateMaterialInwardDTO[] = [...currentMaterials];

    vendorPoItems.forEach(po => {
      po.items.forEach(item => {
        const key = `${po.id}-${item.id}`;
        const selection = poItemSelections[key];
        if (selection?.selected && selection.quantity > 0) {
          const quantity = selection.quantity;
          const material: CreateMaterialInwardDTO = {
            material_type: item.material_type as any,
            quantity,
            uom: item.uom,
            rate: item.rate,
            amount: quantity * item.rate,
            material_quality: item.material_quality || undefined,
            is_sample: item.is_sample || false,
            image_url: item.image_url || undefined,
            fabric_width: item.fabric_width ?? null,
            fabric_L: item.fabric_L ?? null,
            material_code: '',
            po_item_id: item.id
          };
          const materialIndex = newMaterials.length;
          newMaterials.push(material);
        }
      });
    });

    const totalAmount = calculateTotalAmount(newMaterials);

    setPurchaseData(prev => ({
      ...prev,
      material_inwards: newMaterials,
      total_amount: totalAmount,
      po_id: linkedPO.id
    }));

    setPoItemsDialogOpen(false);
  };

  const calculateTotalAmount = (materialInwards: CreateMaterialInwardDTO[]) => {
    return materialInwards.reduce((total, material) => {
      return total + (material.amount || 0);
    }, 0);
  };

  const handleMaterialChange = (index: number, field: string, value: any) => {
    const currentMaterials = purchaseData.material_inwards || [];
    const updatedMaterials = [...currentMaterials];
    const material = { ...updatedMaterials[index] };
    
    (material as any)[field] = value;
    
    // Auto-calculate amount when quantity or rate changes
    if (field === 'quantity' || field === 'rate') {
      const quantity = field === 'quantity' ? (value || 0) : (material.quantity || 0);
      const rate = field === 'rate' ? (value || 0) : (material.rate || 0);
      material.amount = quantity * rate;
    }
    
    updatedMaterials[index] = material;
    
    const totalAmount = calculateTotalAmount(updatedMaterials);
    
    setPurchaseData(prev => ({
      ...prev,
      material_inwards: updatedMaterials,
      total_amount: totalAmount
    }));
  };

  const addMaterial = () => {
    setPurchaseData(prev => ({
      ...prev,
      material_inwards: [
        ...(prev.material_inwards || []),
        { ...emptyMaterial }
      ]
    }));
  };

  const removeMaterial = (index: number) => {
    const currentMaterials = purchaseData.material_inwards || [];
    if (currentMaterials.length <= 1) return;
    
    const updatedMaterials = currentMaterials.filter((_, i) => i !== index);
    const totalAmount = calculateTotalAmount(updatedMaterials);
    
    setPurchaseData(prev => ({
      ...prev,
      material_inwards: updatedMaterials,
      total_amount: totalAmount
    }));
  };

  const getVendorName = (vendorId: number) => {
    const vendor = vendors.find(v => v.id === vendorId);
    return vendor ? vendor.registered_name : 'Unknown Vendor';
  };

  // Helper to get material type display name
  const getMaterialTypeName = (type: string) => {
    switch (type) {
      case 'FABRIC': return 'Fabric';
      case 'LACE': return 'Lace';
      case 'BUTTON': return 'Button';
      default: return type;
    }
  };

  // Function to handle file upload
  const handleFileUpload = async (index: number, file: File) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      setUploadingImage({...uploadingImage, [index]: true});
      const response = await api.post('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const imageUrl = response.data.url;
      handleMaterialChange(index, 'image_url', imageUrl);
      setUploadingImage({...uploadingImage, [index]: false});
      setSuccess('Image uploaded successfully');
    } catch (err) {
      console.error('Error uploading image:', err);
      setError('Failed to upload image. Please try again.');
      setUploadingImage({...uploadingImage, [index]: false});
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy');
    } catch {
      return dateString;
    }
  };

  // Filter purchases by search
  const filteredPurchases = purchases.filter(purchase => {
    const search = purchaseSearch.trim().toLowerCase();
    if (!search) return true;
    
    const vendor = vendors.find(v => v.id === purchase.vendor_id);
    const vendorName = vendor ? vendor.registered_name.toLowerCase() : '';
    
    return (
      purchase.invoice_id?.toLowerCase().includes(search) ||
      vendorName.includes(search) ||
      purchase.notes?.toLowerCase().includes(search)
    );
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const errors: {[key: string]: string} = {};
    
    if (purchaseData.vendor_id === 0) {
      errors.vendor_id = 'Vendor is required';
    }
    
    if (!purchaseData.invoice_id?.trim()) {
      errors.invoice_id = 'Invoice ID is required';
    }
    
    // Filter out blank/empty rows - a row is considered valid if it has quantity > 0 or rate > 0 or quality filled
    const validMaterials = (purchaseData.material_inwards || []).filter(material => 
      (material.quantity && material.quantity > 0) || 
      (material.rate && material.rate > 0) || 
      (material.material_quality && material.material_quality.trim() !== '')
    );
    
    if (validMaterials.length === 0) {
      errors.materials = 'At least one material with quantity is required';
    }
    
    if (Object.keys(errors).length > 0) {
    setFormErrors(errors);
      return;
    }
    
    try {
      setLoading(true);
      setFormErrors({});
      
      const materialsForCreation: CreateMaterialInwardDTO[] = validMaterials;
      if (materialsForCreation.length === 0) {
        throw new Error('No valid materials to process');
      }

      // Build PO item fulfillments directly from tagged materials
      const poItemFulfillments =
        materialsForCreation
          .map((material, index) => {
            if (!material.po_item_id) {
              return null;
            }
            return {
              po_item_id: material.po_item_id,
              material_index: index,
              quantity: material.quantity || 0
            };
          })
          .filter(Boolean) as CreatePurchaseDTO['po_item_fulfillments'];

      const formData: CreatePurchaseDTO = {
        ...purchaseData,
        material_inwards: materialsForCreation,
        total_amount: calculateTotalAmount(materialsForCreation),
        po_item_fulfillments: poItemFulfillments
      };
      
      if (isEditMode && selectedPurchase) {
        const updateData = {
          ...formData,
          updateMode: updateMode
        };
        await purchaseApi.update(selectedPurchase.id, updateData);
        setSuccess('Purchase updated successfully');
      } else {
        await purchaseApi.create(formData);
        setSuccess('Purchase created successfully');
      }
      
      fetchPurchases();
        handleCloseDialog();
      setLoading(false);
    } catch (err: any) {
      console.error('Error submitting purchase:', err);
      setError(err.response?.data?.error || err.message || 'Failed to process purchase');
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      <Paper elevation={3} sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Purchase Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleNewPurchase}
            sx={{ px: 3 }}
          >
            New Purchase
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Search and Filter Controls */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  size="small"
            label="Search purchases..."
            value={purchaseSearch}
            onChange={(e) => setPurchaseSearch(e.target.value)}
            sx={{ minWidth: 300 }}
                  InputProps={{
                    endAdornment: purchaseSearch && (
                <IconButton size="small" onClick={() => setPurchaseSearch('')}>
                  <ClearIcon />
                      </IconButton>
                    )
                  }}
                />
              </Box>

        {loading && filteredPurchases.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : isMobile ? (
          <Box>
              {filteredPurchases.map((purchase) => (
                <Card key={purchase.id} sx={{ mb: 2, borderRadius: 2, boxShadow: 1 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {purchase.invoice_id}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Vendor: {getVendorName(purchase.vendor_id)}
                    </Typography>
                      <Typography variant="body2" color="text.secondary">
                      Invoice Date: {formatDate(purchase.invoice_date)}
                    </Typography>
                      <Typography variant="body2" color="text.secondary">
                      Received Date: {formatDate(purchase.received_date)}
                    </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
                        Total: ₹{purchase.total_amount.toLocaleString('en-IN')}
                    </Typography>
                  </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <IconButton size="small" color="primary" onClick={() => handleViewPurchase(purchase)}>
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton size="small" color="info" onClick={() => handleEditPurchase(purchase)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(purchase)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
                </Card>
              ))}
            </Box>
        ) : (
          <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice ID</TableCell>
                    <TableCell>Vendor</TableCell>
                    <TableCell>Invoice Date</TableCell>
                    <TableCell>Received Date</TableCell>
                    <TableCell align="right">Total Amount</TableCell>
                  <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPurchases.map((purchase) => (
                    <TableRow key={purchase.id}>
                    <TableCell sx={{ fontWeight: 500 }}>{purchase.invoice_id}</TableCell>
                      <TableCell>{getVendorName(purchase.vendor_id)}</TableCell>
                      <TableCell>{formatDate(purchase.invoice_date)}</TableCell>
                      <TableCell>{formatDate(purchase.received_date)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      ₹{purchase.total_amount.toLocaleString('en-IN')}
                      </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="View">
                          <IconButton 
                            size="small" 
                            color="primary" 
                            onClick={() => handleViewPurchase(purchase)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton 
                            size="small" 
                            color="info"
                            onClick={() => handleEditPurchase(purchase)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton 
                            size="small" 
                            color="error" 
                            onClick={() => handleDeleteClick(purchase)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
        )}

        {/* Pagination */}
        {purchaseTotal > purchasePageSize && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={Math.ceil(purchaseTotal / purchasePageSize)}
                page={purchasePage}
                onChange={(_e, value) => setPurchasePage(value)}
                color="primary"
                shape="rounded"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          )}

                {filteredPurchases.length === 0 && !loading && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="textSecondary">
              {purchaseSearch ? 'No purchases match your search criteria.' : 'No purchases found. Create your first purchase to get started.'}
            </Typography>
            </Box>
          )}
      </Paper>

      {/* Purchase Form Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth={false}
        sx={{
          '& .MuiDialog-paper': {
            width: { xs: '95%', sm: '90%', md: '80%' },
            maxWidth: '1200px',
            height: { xs: '95vh', sm: '90vh' },
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>
          {isEditMode ? 'Edit Purchase' : 'New Purchase'}
        </DialogTitle>
        <DialogContent sx={{ overflow: 'auto' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                options={vendors}
                getOptionLabel={(v: Vendor) => v.registered_name + (v.alias ? ` (${v.alias})` : '')}
                isOptionEqualToValue={(opt: Vendor, val: Vendor) => opt.id === val.id}
                value={vendors.find((v: Vendor) => v.id === purchaseData.vendor_id) || null}
                onChange={(_e, newVal: Vendor | null) => {
                  setPurchaseData(prev => ({ ...prev, vendor_id: newVal ? newVal.id : 0 }));
                }}
                filterOptions={(opts: Vendor[], state) => {
                  const q = state.inputValue.toLowerCase();
                  return opts.filter((v: Vendor) =>
                    v.registered_name.toLowerCase().includes(q) ||
                    (v.alias || '').toLowerCase().includes(q) ||
                    (v.phone || '').includes(q)
                  );
                }}
                renderOption={(props, option: Vendor) => (
                  <Box component="li" {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{option.registered_name}</Typography>
                      {option.alias && <Typography variant="caption" color="text.secondary">{option.alias}</Typography>}
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vendor"
                    error={!!formErrors.vendor_id}
                    helperText={formErrors.vendor_id || ''}
                    placeholder="Search vendor..."
                  />
                )}
              />
              {purchaseData.vendor_id !== 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={openPoItemsDialog}
                  >
                    Link PO Items
                  </Button>
                  {linkedPO && (
                    <Chip
                      size="small"
                      color="primary"
                      label={`Linked to PO ${linkedPO.po_number}`}
                    />
                  )}
                </Box>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Invoice ID"
                name="invoice_id"
                value={purchaseData.invoice_id}
                onChange={handleTextInputChange}
                error={!!formErrors.invoice_id}
                helperText={formErrors.invoice_id}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Invoice Date"
                  value={new Date(purchaseData.invoice_date)}
                  onChange={(date) => {
                    if (date) {
                      setPurchaseData(prev => ({
                        ...prev,
                        invoice_date: format(date, 'yyyy-MM-dd')
                      }));
                    }
                  }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Received Date"
                  value={new Date(purchaseData.received_date)}
                  onChange={(date) => {
                    if (date) {
                      setPurchaseData(prev => ({
                        ...prev,
                        received_date: format(date, 'yyyy-MM-dd')
                      }));
                    }
                  }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Notes"
                name="notes"
                value={purchaseData.notes}
                onChange={handleTextInputChange}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>

          {/* Professional ERP-Style Materials Grid */}
          <Box sx={{ mt: 3, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 600 }}>
                📋 Materials ({(purchaseData.material_inwards || []).length})
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addMaterial}
                  sx={{ backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#1565c0' } }}
                >
                  Add Row
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const newMaterials: CreateMaterialInwardDTO[] = [];
                    for (let i = 0; i < 5; i++) {
                      newMaterials.push({ ...emptyMaterial });
                    }
                    setPurchaseData(prev => ({
                      ...prev,
                      material_inwards: [...prev.material_inwards, ...newMaterials]
                    }));
                  }}
                  sx={{ color: '#1976d2', borderColor: '#1976d2' }}
                >
                  +5 Rows
                </Button>
              </Box>
            </Box>
            
            <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
              <Box sx={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6', p: 1.5 }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={0.5}><Typography variant="caption" fontWeight="bold">#</Typography></Grid>
                  <Grid item xs={1.8}><Typography variant="caption" fontWeight="bold">MATERIAL CODE</Typography></Grid>
                  <Grid item xs={1.2}><Typography variant="caption" fontWeight="bold">TYPE</Typography></Grid>
                  <Grid item xs={1.3}><Typography variant="caption" fontWeight="bold">QUALITY</Typography></Grid>
                  <Grid item xs={1}><Typography variant="caption" fontWeight="bold">QTY</Typography></Grid>
                  <Grid item xs={0.8}><Typography variant="caption" fontWeight="bold">UOM</Typography></Grid>
                  <Grid item xs={1}><Typography variant="caption" fontWeight="bold">RATE</Typography></Grid>
                  <Grid item xs={1}><Typography variant="caption" fontWeight="bold">AMOUNT</Typography></Grid>
                  <Grid item xs={0.8}><Typography variant="caption" fontWeight="bold">WIDTH</Typography></Grid>
                  <Grid item xs={0.8}><Typography variant="caption" fontWeight="bold">LENGTH</Typography></Grid>
                  <Grid item xs={0.7}><Typography variant="caption" fontWeight="bold">SAMPLE</Typography></Grid>
                  <Grid item xs={1}><Typography variant="caption" fontWeight="bold">IMAGE</Typography></Grid>
                  <Grid item xs={0.5}><Typography variant="caption" fontWeight="bold">⚙️</Typography></Grid>
                </Grid>
              </Box>

              <Box sx={{ maxHeight: '50vh', overflowY: 'auto' }}>
                {(purchaseData.material_inwards || []).map((material, index) => (
                  <Box key={index} sx={{ 
                    borderBottom: '1px solid #e9ecef',
                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                    '&:hover': { backgroundColor: '#e3f2fd' },
                    p: 1.5
                  }}>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={0.5}>
                        <Box
                          sx={{
                            minWidth: '32px', 
                            height: '24px', 
                            fontSize: '12px',
                            backgroundColor: '#e3f2fd', 
                            color: '#1976d2', 
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '12px'
                          }}
                        >
                          {index + 1}
                        </Box>
                      </Grid>
                      <Grid item xs={1.8}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TextField
                            size="small"
                            value={material.material_code || ''}
                            onChange={(e) => handleMaterialChange(index, 'material_code', e.target.value)}
                            placeholder="Auto-generate"
                            sx={{ flex: 1, '& .MuiOutlinedInput-root': { fontSize: '13px' } }}
                          />
                          <Tooltip title="Copy existing code">
                            <IconButton size="small" onClick={() => {
                              const code = prompt('Enter existing material code:');
                              if (code) handleMaterialChange(index, 'material_code', code);
                            }} sx={{ width: '28px', height: '28px' }}>📋</IconButton>
                          </Tooltip>
                        </Box>
                      </Grid>
                      <Grid item xs={1.2}>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={material.material_type}
                            onChange={(e) => handleMaterialChange(index, 'material_type', e.target.value)}
                            sx={{ fontSize: '13px' }}
                          >
                            <MenuItem value={MaterialType.FABRIC}>Fabric</MenuItem>
                            <MenuItem value={MaterialType.LACE}>Lace</MenuItem>
                            <MenuItem value={MaterialType.BUTTON}>Button</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={1.3}>
                        <Autocomplete
                          freeSolo
                          size="small"
                          options={materialQualityOptions}
                          value={material.material_quality || ''}
                          onInputChange={(_event, newInputValue) =>
                            handleMaterialChange(index, 'material_quality', newInputValue)
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Quality"
                              sx={{ '& .MuiOutlinedInput-root': { fontSize: '13px' } }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={1}>
                        <TextField
                          size="small" 
                          type="number"
                          inputMode="decimal"
                          value={material.quantity || ''}
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            const qty = inputValue === '' ? 0 : parseFloat(inputValue) || 0;
                            handleMaterialChange(index, 'quantity', qty);
                          }}
                          placeholder="0"
                          disabled={false}
                          sx={{ '& .MuiOutlinedInput-root': { fontSize: '13px' } }}
                        />
                      </Grid>
                      <Grid item xs={0.8}>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={material.uom}
                            onChange={(e) => handleMaterialChange(index, 'uom', e.target.value)}
                            sx={{ fontSize: '13px' }}
                          >
                            <MenuItem value="mtr">mtr</MenuItem>
                            <MenuItem value="pcs">pcs</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={1}>
                        <TextField
                          size="small" 
                          type="number"
                          inputMode="decimal"
                          value={material.rate || ''}
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            const rate = inputValue === '' ? 0 : parseFloat(inputValue) || 0;
                            handleMaterialChange(index, 'rate', rate);
                          }}
                          placeholder="0.00"
                          disabled={false}
                          sx={{ '& .MuiOutlinedInput-root': { fontSize: '13px' } }}
                        />
                      </Grid>
                      <Grid item xs={1}>
                        <TextField
                          size="small" type="number"
                          value={material.amount || ''}
                          placeholder="0.00"
                          disabled={true}
                          InputProps={{ readOnly: true }}
                          sx={{ '& .MuiOutlinedInput-root': { fontSize: '13px', backgroundColor: '#f8f9fa' } }}
                        />
                      </Grid>
                      <Grid item xs={0.8}>
                        <TextField
                          size="small" type="number"
                          value={material.fabric_width || ''}
                          onChange={(e) => handleMaterialChange(index, 'fabric_width', e.target.value ? parseFloat(e.target.value) : null)}
                          placeholder="0"
                          sx={{ '& .MuiOutlinedInput-root': { fontSize: '13px' } }}
                        />
                      </Grid>
                      <Grid item xs={0.8}>
                        <TextField
                          size="small" type="number"
                          value={material.fabric_L || ''}
                          onChange={(e) => handleMaterialChange(index, 'fabric_L', e.target.value ? parseFloat(e.target.value) : null)}
                          placeholder="0"
                          sx={{ '& .MuiOutlinedInput-root': { fontSize: '13px' } }}
                        />
                      </Grid>
                      <Grid item xs={0.7}>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Checkbox
                            size="small"
                            checked={material.is_sample || false}
                            onChange={(e) => handleMaterialChange(index, 'is_sample', e.target.checked)}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={1}>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'center' }}>
                          <Tooltip title="Upload Image">
                            <IconButton
                              size="small"
                              onClick={() => document.getElementById(`file-input-${index}`)?.click()}
                              disabled={uploadingImage[index]}
                              sx={{ color: material.image_url ? '#28a745' : '#6c757d', width: '32px', height: '32px' }}
                            >
                              {uploadingImage[index] ? <CircularProgress size={16} /> : 
                               material.image_url ? '📷' : <UploadIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <input
                            id={`file-input-${index}`}
                            type="file" accept="image/*"
                            onChange={(e) => {
                              if (e.target.files && e.target.files[0]) {
                                handleFileUpload(index, e.target.files[0]);
                              }
                            }}
                            style={{ display: 'none' }}
                          />
                          {material.image_url && (
                            <Tooltip title="View Image">
                              <IconButton size="small" onClick={() => window.open(material.image_url, '_blank')}
                                sx={{ color: '#1976d2', width: '24px', height: '24px' }}>
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={0.5}>
                        <Tooltip title="Delete Row">
                          <IconButton 
                            size="small" color="error"
                            onClick={() => removeMaterial(index)}
                            disabled={(purchaseData.material_inwards || []).length === 1}
                            sx={{ width: '32px', height: '32px' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Box>

              <Box sx={{ backgroundColor: '#f8f9fa', borderTop: '2px solid #dee2e6', p: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={8}>
                    <Typography variant="body2" fontWeight="600">
                      📊 Total Materials: {(purchaseData.material_inwards || []).length} | 
                      ⚖️ Qty: {((purchaseData.material_inwards || []).reduce((sum, m) => sum + (m.quantity || 0), 0) || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={4} sx={{ textAlign: 'right' }}>
                    <Typography variant="body1" color="#1976d2" fontWeight="700">
                      💰 Total: ₹{((purchaseData.material_inwards || []).reduce((sum, material) => sum + (material.amount || 0), 0) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, p: 2, backgroundColor: 'primary.light', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ color: 'primary.contrastText' }}>
              Total Amount:
                    </Typography>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.contrastText' }}>
              ₹{purchaseData.total_amount.toLocaleString('en-IN')}
                    </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : (isEditMode ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* PO Items Selection Dialog */}
      <Dialog
        open={poItemsDialogOpen}
        onClose={() => setPoItemsDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Select PO Items to Add</DialogTitle>
        <DialogContent dividers>
          {vendorPoItems.length === 0 ? (
            <Typography>No pending PO items found for this vendor.</Typography>
          ) : (
            vendorPoItems.map(po => (
              <Box key={po.id} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  PO {po.po_number} ({po.status})
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell>Item</TableCell>
                      <TableCell align="right">Ordered</TableCell>
                      <TableCell align="right">Fulfilled</TableCell>
                      <TableCell align="right">Pending</TableCell>
                      <TableCell align="right">Rate</TableCell>
                      <TableCell align="right">Purchase Qty</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {po.items.map(item => {
                      const key = `${po.id}-${item.id}`;
                      const selection = poItemSelections[key];
                      const pending = item.remaining_qty ?? (item.quantity - (item.fulfilled_qty || 0));
                      return (
                        <TableRow key={item.id}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={!!selection?.selected}
                              onChange={(e) => togglePoItemSelection(po.id, item, e.target.checked)}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {item.material_type}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item.material_quality || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right">{item.fulfilled_qty || 0}</TableCell>
                          <TableCell align="right">{pending}</TableCell>
                          <TableCell align="right">{item.rate.toFixed(2)}</TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small"
                              type="number"
                              inputProps={{ min: 0, max: pending, step: '0.01' }}
                              value={selection?.quantity ?? pending}
                              onChange={(e) =>
                                updatePoItemSelectionQty(
                                  po.id,
                                  item,
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              disabled={!selection?.selected}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPoItemsDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={applySelectedPoItemsToMaterials}
            variant="contained"
            disabled={Object.keys(poItemSelections).length === 0}
          >
            Add Selected Items
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Purchase Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Purchase Details</span>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => {
              setViewDialogOpen(false);
              if (selectedPurchase) {
                handleEditPurchase(selectedPurchase);
              }
            }}
          >
            Edit
          </Button>
        </DialogTitle>
            <DialogContent>
          {selectedPurchase && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1"><strong>Invoice ID:</strong></Typography>
                  <Typography variant="body1">{selectedPurchase.invoice_id}</Typography>
                </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1"><strong>Vendor:</strong></Typography>
                <Typography variant="body1">{getVendorName(selectedPurchase.vendor_id)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1"><strong>Invoice Date:</strong></Typography>
                  <Typography variant="body1">{formatDate(selectedPurchase.invoice_date)}</Typography>
                </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1"><strong>Received Date:</strong></Typography>
                  <Typography variant="body1">{formatDate(selectedPurchase.received_date)}</Typography>
                </Grid>
              <Grid item xs={12}>
                <Typography variant="body1"><strong>Notes:</strong></Typography>
                <Typography variant="body1">{selectedPurchase.notes || 'No notes'}</Typography>
                </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Materials</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Code</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Quality</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell>UOM</TableCell>
                      <TableCell align="right">Rate</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                      {selectedPurchase.material_inwards?.map((material: any, index: number) => (
                        <TableRow key={index}>
                        <TableCell>{material.material_code}</TableCell>
                        <TableCell>{getMaterialTypeName(material.material_type)}</TableCell>
                        <TableCell>{material.material_quality || '-'}</TableCell>
                        <TableCell align="right">{material.quantity}</TableCell>
                        <TableCell>{material.uom}</TableCell>
                        <TableCell align="right">₹{material.rate?.toLocaleString('en-IN')}</TableCell>
                        <TableCell align="right">₹{material.amount?.toLocaleString('en-IN')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Total: ₹{selectedPurchase.total_amount?.toLocaleString('en-IN')}
                          </Typography>
                        </Box>
                      </Grid>
                </Grid>
          )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
            </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this purchase? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeletePurchase} color="error" variant="contained">
            {loading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default PurchasePage; 